import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import { ActivityMemberPicker } from "@/components/activity-levels/member-picker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAdjustXpMutation, useProgressQuery } from "@/hooks/activity-levels"
import { type AdjustXpOperation } from "@/lib/activity-levels-api"

export const Route = createFileRoute("/_authed/_admin/activity-levels/members")(
  {
    component: ActivityLevelsMembersRoute,
  },
)

function ActivityLevelsMembersRoute() {
  const track = "message" as const
  const [memberDisplayName, setMemberDisplayName] = useState<string | null>(
    null,
  )
  const [userId, setUserId] = useState<string | null>(null)
  const [operation, setOperation] = useState<AdjustXpOperation>("add")
  const [amount, setAmount] = useState("")

  const progressQuery = useProgressQuery(track, userId)
  const adjust = useAdjustXpMutation(track, userId ?? "")
  const progress = progressQuery.data ?? null

  return (
    <div className="grid gap-4 md:gap-5">
      <Card>
        <CardHeader>
          <CardTitle>Member XP</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <ActivityMemberPicker
            id="member-search"
            onSelect={(member) => {
              setUserId(member.memberId)
              setMemberDisplayName(member.displayName)
            }}
          />
          {userId && (
            <p className="text-sm text-muted-foreground">
              Selected: {memberDisplayName ?? userId} · {userId}
            </p>
          )}
        </CardContent>
      </Card>

      {userId && (
        <Card>
          <CardHeader>
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {progressQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : progressQuery.isError ? (
              <StatusAlert tone="danger">
                Failed to load member progress.
              </StatusAlert>
            ) : progress == null ? (
              <p className="text-sm text-muted-foreground">
                No XP recorded for this member on this track.
              </p>
            ) : (
              <div className="grid gap-1">
                <div className="text-2xl font-semibold">
                  Level {progress.level}
                </div>
                <div className="text-sm text-muted-foreground">
                  {progress.totalXp} total XP · {progress.displayName}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2 border-t pt-4">
              <div className="grid gap-1.5">
                <Label htmlFor="adjust-op">Operation</Label>
                <Select
                  value={operation}
                  onValueChange={(value) =>
                    setOperation((value ?? "add") as AdjustXpOperation)
                  }
                >
                  <SelectTrigger id="adjust-op" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add</SelectItem>
                    <SelectItem value="remove">Remove</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="reset">Reset</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="adjust-amount">Amount</Label>
                <Input
                  id="adjust-amount"
                  type="number"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  disabled={operation === "reset"}
                  className="w-32"
                />
              </div>
              <Button
                disabled={
                  adjust.isPending ||
                  (operation !== "reset" && amount.trim() === "")
                }
                onClick={() => {
                  adjust.mutate(
                    {
                      operation,
                      amount:
                        operation === "reset" ? undefined : Number(amount),
                    },
                    { onSuccess: () => setAmount("") },
                  )
                }}
              >
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
